import React from "react";
import PropTypes from "prop-types";
import DOMPurify from "dompurify";

import ImageCropped from "views_shared/app/javascript/components/ImageCropped";
import { isPitchRead } from "views_shared/app/javascript/utils/pitchStateHelpers";
import formatLastSentAt from "../ConversationsList/Conversation/formatLastSentAt";
import stripHtmlTags from "../ConversationsList/Conversation/stripHtmlTags";
import { isFastPitch } from "../fastPitch";
import PitchBadges from "./PitchBadges";

const videoAttachment = (pitch) =>
  (pitch.pitch_attachments || []).find((a) => a.type === "Interact::Attachment::Video");

const videoDurationLabel = (attachment) => {
  const seconds = Number(attachment?.metadata?.duration);
  if (!seconds) return null;
  const total = Math.round(seconds); // round first: floor(119.6/60) + round(59.6) rendered "1:60"
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

// A conversationless pitch as a list row — pitch-centric, but borrowing the inbox
// Conversation row's utils and markup so the two row types read as one list. Used by the
// request page's Pitch Inbox rail and the global inbox's Everything tab (virtual rows).
function PitchRow({
  pitch,
  selected = false,
  onSelect,
  requestTitle = null,
  // The request's creation time, for the Fast Pitch badge — passed by the request page's
  // rail; absent (and the badge with it) where rows don't know their request's timestamps.
  requestCreatedAt = null,
  // Muted/reported overrides for rows whose pitch objects don't carry the flags (the
  // messaging inbox computes them from the lifted muteReport lists); default to the
  // V1-serialized fields the request page provides.
  muted = null,
  reported = null,
  actions = null,
  selectionMode = false,
  isSelected = false,
  onToggleSelection = null,
}) {
  const video = videoAttachment(pitch);
  const duration = videoDurationLabel(video);
  // Inbox rows carry V2's user_under_audit_at; V1's request-page rail passes V1 records,
  // which carry under_audit — one component serves both (keep in sync with the V1 mirror).
  const audited = !!(pitch.user_under_audit_at || pitch.under_audit);
  // Figma "Quick Reply": pill badges under the title. Flagged = the SPAM-flagged user
  // state (pairs with the Flagged User banner); audit has its own caution treatment.
  const fast = isFastPitch(pitch.sent_at, requestCreatedAt, pitch.pitch_text);
  const flagged = !!pitch.spam_flagged;
  const isMuted = muted ?? !!pitch.muted;
  const isReported = reported ?? !!pitch.reported;

  // Bulk-manage: row clicks toggle membership instead of selecting (Conversation parity).
  const handleActivate = (e) => {
    if (selectionMode && onToggleSelection) {
      onToggleSelection(`pitch-${pitch.id}`);
      return;
    }
    onSelect(e);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      data-conversation-id={`pitch-${pitch.id}`}
      className={`pi-row position-relative ${selected ? "selected" : ""}`}
      onClick={handleActivate}
      onKeyDown={(e) => {
        // Only act on keys aimed at the row itself — the actions slot holds a nested
        // dropdown button whose Space/Enter must not be swallowed here.
        if (e.target !== e.currentTarget) return;

        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleActivate(e);
        }
      }}
    >
      {/* Inbox Conversation row's unread-dot markup */}
      {!isPitchRead(pitch) && !selectionMode && (
        <span
          className="position-absolute bg-accent-blue-60 rounded-circle"
          aria-label="Unread"
          style={{ width: 12, height: 12, left: 12, top: "50%", transform: "translateY(-50%)", zIndex: 1 }}
        />
      )}

      {/* Checkbox in selection mode — same markup as the Conversation row's */}
      {selectionMode && (
        <div
          className="position-absolute"
          style={{ left: "8px", top: "50%", transform: "translateY(-50%)", zIndex: 1 }}
        >
          <input
            type="checkbox"
            className="form-check-input"
            checked={isSelected}
            readOnly
            style={{ cursor: "pointer", width: 18, height: 18 }}
            tabIndex={-1}
          />
        </div>
      )}
      <div className="pi-row-top">
        <ImageCropped
          roundedCircle
          src={pitch.profile_image_url}
          alt={pitch.profile_name}
          size={42}
          fallbackToInitials
        />
        <div className="pi-row-who">
          <strong className="paragraph-lg text-body text-truncate d-block">{pitch.profile_name}</strong>
          {(pitch.profile_title_at_workplace || pitch.profile_workplace) && (
            <p className="paragraph-sm text-secondary mb-0 text-truncate">
              {pitch.profile_title_at_workplace || pitch.profile_workplace}
            </p>
          )}
          {(fast || flagged || isMuted || isReported) && (
            <div className="d-flex flex-wrap gap-2 mt-1">
              <PitchBadges fast={fast} flagged={flagged} muted={isMuted} reported={isReported} />
            </div>
          )}
        </div>
        {actions ?? <span className="paragraph-xs text-secondary text-nowrap">{formatLastSentAt(pitch.sent_at)}</span>}
      </div>
      {/* Inbox row's pitch-origin subject-line markup */}
      <p className="d-flex align-items-center paragraph-md fw-semibold text-body mb-1 mt-2">
        <i className="fa-solid fa-megaphone me-2 text-primary-60" aria-hidden="true" />
        <span className="text-truncate">{requestTitle || pitch.request_title}</span>
      </p>
      {/* Audited pitcher: don't leak the pitch text (or its search excerpt) — the same
          caution line as the request page's rail */}
      {audited && (
        <p className="paragraph-md mb-0 text-start text-danger">
          <i className="fa-solid fa-circle-exclamation me-2" aria-hidden="true" />
          Flagged by other media users &mdash; review with caution
        </p>
      )}
      {!audited && pitch.search_hits?.length > 0 && (
        <div
          className="paragraph-md text-body mb-0 text-truncate text-start"
          // Safe: content is server-generated ts_headline HTML with <mark> tags,
          // sanitized by DOMPurify to strip anything beyond simple formatting.
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(pitch.search_hits.map((hit) => hit.content).join(" ... ")),
          }}
        />
      )}
      {!audited && !pitch.search_hits?.length && (
        <p className="paragraph-md text-body mb-0 text-truncate text-start">
          {video ? (
            <>
              <i className="fa-regular fa-circle-play me-2" aria-hidden="true" />
              Video Recording{duration ? ` · ${duration}` : ""}
            </>
          ) : (
            stripHtmlTags(pitch.pitch_text)
          )}
        </p>
      )}
    </div>
  );
}

PitchRow.propTypes = {
  pitch: PropTypes.shape({
    id: PropTypes.number.isRequired,
    profile_name: PropTypes.string,
    profile_image_url: PropTypes.string,
    profile_workplace: PropTypes.string,
    profile_title_at_workplace: PropTypes.string,
    pitch_text: PropTypes.string,
    sent_at: PropTypes.string,
    request_title: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types -- pitches-view jsonb rows
    pitch_attachments: PropTypes.array,
    search_hits: PropTypes.arrayOf(PropTypes.shape({ content: PropTypes.string })),
  }).isRequired,
  selected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  requestTitle: PropTypes.string,
  requestCreatedAt: PropTypes.string,
  muted: PropTypes.bool,
  reported: PropTypes.bool,
  actions: PropTypes.node,
  selectionMode: PropTypes.bool,
  isSelected: PropTypes.bool,
  onToggleSelection: PropTypes.func,
};

export default PitchRow;
